// src/components/storico/StoricoPersonalizzatiTable.tsx
import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { Table, Badge, Text, Group, Box, ScrollArea, Tooltip } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { StoricoProgettoPersonalizzati } from '../../services/storico-personalizzati-service';
import { ColumnResizer } from '../data-table/ColumnResizer';
import { RESIZABLE_TABLE_OPTIONS, useColumnSizing } from '../../hooks/use-column-sizing';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface StoricoPersonalizzatiTableProps {
  data: StoricoProgettoPersonalizzati[];
  onRowClick?: (progetto: StoricoProgettoPersonalizzati) => void;
}

export function StoricoPersonalizzatiTable({ data, onRowClick }: StoricoPersonalizzatiTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created', desc: true }
  ]);
  const { columnSizing, onColumnSizingChange } = useColumnSizing('storico-personalizzati');

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return '-';
    }
  };

  const columns = useMemo<ColumnDef<StoricoProgettoPersonalizzati>[]>(
    () => [
      {
        accessorKey: 'titoloProgetto',
        header: 'Titolo Progetto',
        size: 300,
        cell: ({ getValue }) => {
          const value = (getValue() as string) || '-';
          return (
            <Tooltip label={value} multiline w={350} withArrow disabled={!value || value.length < 40}>
              <Text size="sm" lineClamp={2}>{value}</Text>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'idProgressivo',
        header: 'ID Progressivo',
        size: 140,
        cell: ({ getValue }) => (
          <Text size="sm" c="dimmed">{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        accessorKey: 'tipologiaProgetto',
        header: 'Tipologia',
        size: 160,
        cell: ({ getValue }) => (
          <Text size="sm">{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        id: 'cliente',
        header: 'Cliente',
        size: 180,
        accessorFn: (row) => row.topClient || row.codiceCliente || '',
        cell: ({ getValue }) => (
          <Text size="sm">{(getValue() as string) || '-'}</Text>
        ),
      },
      {
        accessorKey: 'statoFlusso',
        header: 'Stato',
        size: 170,
        cell: ({ getValue }) => {
          const stato = getValue() as string;
          return stato ? <Badge variant="filled" style={{ color: 'white' }}>{stato}</Badge> : <Text size="sm" c="dimmed">-</Text>;
        },
      },
      {
        accessorKey: 'created',
        header: 'Data Creazione',
        size: 140,
        cell: ({ getValue }) => (
          <Text size="sm">{formatDate(getValue() as string)}</Text>
        ),
        sortingFn: 'datetime',
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    ...RESIZABLE_TABLE_OPTIONS,
    state: { sorting, columnSizing },
    onSortingChange: setSorting,
    onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (data.length === 0) {
    return (
      <Text ta="center" c="dimmed" py="xl">
        Nessun dato trovato
      </Text>
    );
  }

  return (
    <ScrollArea>
      <Table striped highlightOnHover withTableBorder layout="fixed" style={{ width: table.getTotalSize(), minWidth: '100%' }}>
        <Table.Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();

                return (
                  <Table.Th
                    key={header.id}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      cursor: canSort ? 'pointer' : 'default',
                      userSelect: canSort ? 'none' : 'auto',
                      width: header.getSize(),
                      position: 'relative',
                    }}
                  >
                    <Group gap={4} wrap="nowrap">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <Box style={{ width: 16, display: 'flex', alignItems: 'center' }}>
                          {sortDirection === 'asc' && <IconChevronUp size={14} />}
                          {sortDirection === 'desc' && <IconChevronDown size={14} />}
                        </Box>
                      )}
                    </Group>
                    <ColumnResizer header={header} />
                  </Table.Th>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {table.getRowModel().rows.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() => onRowClick?.(row.original)}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}
